import { createFileRoute, Outlet, useNavigate, useMatches } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import { useMemo, useState } from 'react';
import { ConfirmDialog, GenericDetailPage, EventInfoBox, Tabs, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEvent } from 'src/api/company';
import { getEventTypes } from 'src/api/admin/types';
import { useConfig } from 'src/hooks/useConfig';
import { useLocalStorage, useBreadcrumbLevel } from '@repo/components/hooks';
import { getCompanyEventAccess } from 'src/helpers/companyEventAccess';

export const Route = createFileRoute('/company_/$groupId/events/$eventId')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isEventArchiveDialogOpen, setIsEventArchiveDialogOpen] = useState(false);
	const { value: locallyStoredBackUrl } = useLocalStorage<string>('companyPortalEventsBackUrl');

	// Determine active tab from URL
	const matches = useMatches();
	const lastMatch = matches[matches.length - 1];
	const selectedSegment = lastMatch?.pathname
		?.replace(`/company/${groupId}/events/${eventId}/`, '')
		?.split('/')[0] as string;

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: typesResponse } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes('?noPagination=true'),
	});

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const types = typesResponse?.data || [];
	const {
		isOwnEvent,
		isOwnGroupEvent,
		isChildEvent,
		isTopLevelEventWithProjectAccess,
		canViewEvent,
	} = getCompanyEventAccess(event, groupId, authenticatedUser?.id);

	useBreadcrumbLevel(1, 'Events', `/company/${groupId}/events`);
	useBreadcrumbLevel(2, event?.name || 'Event Details');
	const { tabs, tabIndexPerTabId } = useMemo(() => {
		const builtTabs = [
			...(event?.type?.Form && event.type.form !== 'event' && canViewEvent
				? [{ id: 'details', label: 'Details' }]
				: []),
			...(!event?.type?.inheritUsersFromGroups && verifyPermission(PERMISSIONS.companyUsersView)
				? [{ id: 'users', label: 'Users' }]
				: []),
			...(event?.projectId && usesSubEvents && canViewEvent
				? [{ id: 'subcontracting', label: 'Subcontracting' }]
				: []),
			...(canViewEvent ? [{ id: 'tasks', label: 'Tasks' }] : []),
			...(verifyPermission(PERMISSIONS.companyDocumentsView) && canViewEvent
				? [{ id: 'documents', label: 'Documents' }]
				: []),
			...(canViewEvent ? [{ id: 'logs', label: 'Logs' }] : []),
		];
		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({ ...acc, [cur.id]: index }),
			{} as Record<string, number>,
		);
		return { tabs: builtTabs, tabIndexPerTabId };
	}, [
		event?.type?.Form,
		event?.type?.form,
		event?.projectId,
		usesSubEvents,
		event?.type?.inheritUsersFromGroups,
		verifyPermission,
		canViewEvent,
	]);
	if (userLoading || eventLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const goBack = () => {
		if (locallyStoredBackUrl) {
			try {
				const url = new URL(locallyStoredBackUrl);
				window.location.href = url.href;
			} catch {
				navigate({ to: '/company/$groupId/events', params: { groupId }, search: {} });
			}
			return;
		}
		navigate({ to: '/company/$groupId/events', params: { groupId }, search: {} });
	};

	const handleEditEvent = async (formData: any) => {
		let isError;
		if (formData) {
			delete formData.type;
			await request
				.put(`/api/company-portal/groups/${groupId}/events/${event.id}`, formData)
				.then(() => {
					EventEmitter.emit('toast', { type: 'success', message: 'Event saved' });
				})
				.catch(() => {
					isError = true;
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to save Event. Did you enter all data correctly?',
					});
				});
		}
		queryClient.invalidateQueries({ queryKey: ['company-event', groupId, eventId] });
		if (!isError) setIsFormOpened(false);
	};

	const archiveEvent = async () => {
		try {
			await request.del(`/api/company-portal/groups/${groupId}/events/${event.id}`, {});
			queryClient.invalidateQueries({ queryKey: ['company-events', groupId] });
			setIsEventArchiveDialogOpen(false);
			goBack();
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to archive event.' });
		}
	};

	const handleTabChange = (newValue: number) => {
		const tab = tabs[newValue];
		if (tab) {
			navigate({
				to: `/company/$groupId/events/$eventId/${tab.id}`,
				params: { groupId, eventId },
			});
		}
	};

	// If we're at the event root, redirect to first tab
	if (!selectedSegment || selectedSegment === eventId) {
		const firstTab = tabs[0];
		if (firstTab) {
			navigate({
				to: `/company/$groupId/events/$eventId/${firstTab.id}`,
				params: { groupId, eventId },
				replace: true,
			});
			return <Spinner className="text-[#1E1E1E]" />;
		}
	}

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content>
					<GenericDetailPage.Content.Notifications>
						{event.status === 'inReview' ? (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Event needs to be reviewed before allowing access
								<ul>{event?.requirements?.map((r: any) => r.issues?.map((i: any) => <li key={i}>{i}</li>))}</ul>
							</Alert>
						) : null}
						{event.status === 'exception' ? (
							<Alert severity="error" variant="filled">
								Exception procedure active — access is denied until the exception is accepted.
								{event.exception?.reason && (
									<>
										<br />
										Reason: {event.exception.reason}
									</>
								)}
							</Alert>
						) : null}
						{event.exception?.isException && event.status !== 'exception' ? (
							<Alert severity="warning" variant="filled">
								This event was accepted via the exception procedure.
								{event.exception?.reason && (
									<>
										<br />
										Exception reason: {event.exception.reason}
									</>
								)}
								{event.exception?.acceptReason && (
									<>
										<br />
										Accept reason: {event.exception.acceptReason}
									</>
								)}
							</Alert>
						) : null}
					</GenericDetailPage.Content.Notifications>
					<GenericDetailPage.Content.Notifications>
						{event.markedForReview?.length && event.markedForReview.includes('invalidMain') ? (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Invalid main company
							</Alert>
						) : null}
					</GenericDetailPage.Content.Notifications>
					<EventInfoBox
						event={event}
						openEditForm={() => setIsFormOpened(true)}
						openArchiveDialog={() => setIsEventArchiveDialogOpen(true)}
						permissions={{
							canEdit: verifyPermission([PERMISSIONS.companyEventsEdit]) && isOwnEvent,
							canArchive: verifyPermission([PERMISSIONS.companyEventsEdit]) && isOwnEvent,
							canDuplicate: verifyPermission([PERMISSIONS.companyEventsCreate]) && !isChildEvent,
							canReview: false,
						}}
						usesSubEvents={usesSubEvents}
					/>
				</GenericDetailPage.Content>

				<Tabs value={tabIndexPerTabId[selectedSegment]} onChange={handleTabChange} className="px-4">
					<Tabs.TabList>
						{tabs.map((tab, index) => (
							<Tabs.Tab key={tab.id} value={index} label={<div className="flex items-center gap-2">{tab.label}</div>} />
						))}
					</Tabs.TabList>
				</Tabs>
				<Outlet />
			</GenericDetailPage>

			<ConfirmDialog
				isOpen={isEventArchiveDialogOpen}
				cancelAction={() => setIsEventArchiveDialogOpen(false)}
				confirmAction={archiveEvent}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this event?
			</ConfirmDialog>

			<EventForm
				isOpen={isFormOpened}
				data={{ ...event }}
				onClose={() => setIsFormOpened(false)}
				onSubmit={handleEditEvent}
				types={types}
				isCompanyPortalEvent={true}
				usesSubEvents={usesSubEvents}
				projectGetUrl={`/api/company-portal/groups/${groupId}/projects/`}
			/>
		</>
	);
}
